import type { CollisionData, Rail, V3 } from './course/builder.js';

export interface RayHit {
  t: number;
  point: V3;
  /** Smooth (interpolated) surface normal. */
  normal: V3;
  /** Geometric face normal. */
  face: V3;
  tri: number;
  flags: number;
}

export interface Contact {
  /** Push-out direction from the surface toward the sphere centre. */
  normal: V3;
  face: V3;
  depth: number;
  tri: number;
  flags: number;
}

const EPS = 1e-9;

/**
 * Static triangle soup with a uniform XZ grid, used for rider ground probes, landing
 * sweeps, wall push-out and camera occlusion. Pure TypeScript so it runs in tests and on
 * the server.
 */
export class CollisionWorld {
  public readonly triangleCount: number;
  private readonly pos: Float32Array;
  private readonly nrm: Float32Array;
  private readonly face: Float32Array;
  private readonly flags: Uint8Array;
  private readonly minX: number;
  private readonly minZ: number;
  private readonly nx: number;
  private readonly nz: number;
  private readonly cellStart: Int32Array;
  private readonly cellItems: Int32Array;
  private readonly stamp: Uint32Array;
  private query = 0;

  public constructor(
    data: CollisionData,
    private readonly cell = 4,
  ) {
    this.pos = data.positions;
    this.nrm = data.normals;
    this.flags = data.flags;
    const count = data.flags.length;
    this.triangleCount = count;
    this.face = new Float32Array(count * 3);
    this.stamp = new Uint32Array(count);
    let minX = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxZ = -Infinity;
    for (let i = 0; i < count; i += 1) {
      const o = i * 9;
      const ax = this.pos[o]!;
      const ay = this.pos[o + 1]!;
      const az = this.pos[o + 2]!;
      const e1x = this.pos[o + 3]! - ax;
      const e1y = this.pos[o + 4]! - ay;
      const e1z = this.pos[o + 5]! - az;
      const e2x = this.pos[o + 6]! - ax;
      const e2y = this.pos[o + 7]! - ay;
      const e2z = this.pos[o + 8]! - az;
      let fx = e1y * e2z - e1z * e2y;
      let fy = e1z * e2x - e1x * e2z;
      let fz = e1x * e2y - e1y * e2x;
      const l = Math.hypot(fx, fy, fz) || 1;
      fx /= l;
      fy /= l;
      fz /= l;
      this.face[i * 3] = fx;
      this.face[i * 3 + 1] = fy;
      this.face[i * 3 + 2] = fz;
      for (let v = 0; v < 3; v += 1) {
        minX = Math.min(minX, this.pos[o + v * 3]!);
        maxX = Math.max(maxX, this.pos[o + v * 3]!);
        minZ = Math.min(minZ, this.pos[o + v * 3 + 2]!);
        maxZ = Math.max(maxZ, this.pos[o + v * 3 + 2]!);
      }
    }
    if (count === 0) {
      minX = 0;
      minZ = 0;
      maxX = 1;
      maxZ = 1;
    }
    this.minX = minX - cell;
    this.minZ = minZ - cell;
    this.nx = Math.ceil((maxX - this.minX) / cell) + 2;
    this.nz = Math.ceil((maxZ - this.minZ) / cell) + 2;
    const counts = new Int32Array(this.nx * this.nz);
    const ranges: [number, number, number, number][] = [];
    for (let i = 0; i < count; i += 1) {
      const o = i * 9;
      const x0 = Math.min(this.pos[o]!, this.pos[o + 3]!, this.pos[o + 6]!);
      const x1 = Math.max(this.pos[o]!, this.pos[o + 3]!, this.pos[o + 6]!);
      const z0 = Math.min(this.pos[o + 2]!, this.pos[o + 5]!, this.pos[o + 8]!);
      const z1 = Math.max(this.pos[o + 2]!, this.pos[o + 5]!, this.pos[o + 8]!);
      const ix0 = this.cellX(x0);
      const ix1 = this.cellX(x1);
      const iz0 = this.cellZ(z0);
      const iz1 = this.cellZ(z1);
      ranges.push([ix0, ix1, iz0, iz1]);
      for (let ix = ix0; ix <= ix1; ix += 1) {
        for (let iz = iz0; iz <= iz1; iz += 1) counts[ix * this.nz + iz]! += 1;
      }
    }
    this.cellStart = new Int32Array(this.nx * this.nz + 1);
    for (let c = 0; c < counts.length; c += 1) {
      this.cellStart[c + 1] = this.cellStart[c]! + counts[c]!;
    }
    this.cellItems = new Int32Array(this.cellStart[counts.length]!);
    const fill = new Int32Array(counts.length);
    for (let i = 0; i < count; i += 1) {
      const [ix0, ix1, iz0, iz1] = ranges[i]!;
      for (let ix = ix0; ix <= ix1; ix += 1) {
        for (let iz = iz0; iz <= iz1; iz += 1) {
          const c = ix * this.nz + iz;
          this.cellItems[this.cellStart[c]! + fill[c]!] = i;
          fill[c]! += 1;
        }
      }
    }
  }

  private cellX(x: number): number {
    return Math.max(0, Math.min(this.nx - 1, Math.floor((x - this.minX) / this.cell)));
  }

  private cellZ(z: number): number {
    return Math.max(0, Math.min(this.nz - 1, Math.floor((z - this.minZ) / this.cell)));
  }

  public triangleFlags(tri: number): number {
    return this.flags[tri] ?? 0;
  }

  /**
   * Casts a ray (direction must be unit length). Only front faces are hit unless
   * `twoSided` is set.
   */
  public raycast(origin: V3, dir: V3, maxDist: number, twoSided = false): RayHit | null {
    this.query = (this.query + 1) >>> 0;
    if (this.query === 0) {
      this.stamp.fill(0);
      this.query = 1;
    }
    const [ox, oy, oz] = origin;
    const [dx, dy, dz] = dir;
    let best = maxDist;
    let bestTri = -1;
    let bestU = 0;
    let bestV = 0;

    let ix = this.cellX(ox);
    let iz = this.cellZ(oz);
    const stepX = dx > 0 ? 1 : -1;
    const stepZ = dz > 0 ? 1 : -1;
    const invDx = Math.abs(dx) > EPS ? 1 / dx : Infinity;
    const invDz = Math.abs(dz) > EPS ? 1 / dz : Infinity;
    const nextBoundaryX = this.minX + (ix + (dx > 0 ? 1 : 0)) * this.cell;
    const nextBoundaryZ = this.minZ + (iz + (dz > 0 ? 1 : 0)) * this.cell;
    let tMaxX = Number.isFinite(invDx) ? (nextBoundaryX - ox) * invDx : Infinity;
    let tMaxZ = Number.isFinite(invDz) ? (nextBoundaryZ - oz) * invDz : Infinity;
    const tDeltaX = Number.isFinite(invDx) ? this.cell * Math.abs(invDx) : Infinity;
    const tDeltaZ = Number.isFinite(invDz) ? this.cell * Math.abs(invDz) : Infinity;

    for (let guard = 0; guard < 4096; guard += 1) {
      const c = ix * this.nz + iz;
      for (let k = this.cellStart[c]!; k < this.cellStart[c + 1]!; k += 1) {
        const tri = this.cellItems[k]!;
        if (this.stamp[tri] === this.query) continue;
        this.stamp[tri] = this.query;
        const o = tri * 9;
        const ax = this.pos[o]!;
        const ay = this.pos[o + 1]!;
        const az = this.pos[o + 2]!;
        const e1x = this.pos[o + 3]! - ax;
        const e1y = this.pos[o + 4]! - ay;
        const e1z = this.pos[o + 5]! - az;
        const e2x = this.pos[o + 6]! - ax;
        const e2y = this.pos[o + 7]! - ay;
        const e2z = this.pos[o + 8]! - az;
        const px = dy * e2z - dz * e2y;
        const py = dz * e2x - dx * e2z;
        const pz = dx * e2y - dy * e2x;
        const det = e1x * px + e1y * py + e1z * pz;
        if (twoSided ? Math.abs(det) < EPS : det < EPS) continue;
        const inv = 1 / det;
        const tx = ox - ax;
        const ty = oy - ay;
        const tz = oz - az;
        const u = (tx * px + ty * py + tz * pz) * inv;
        if (u < -1e-6 || u > 1 + 1e-6) continue;
        const qx = ty * e1z - tz * e1y;
        const qy = tz * e1x - tx * e1z;
        const qz = tx * e1y - ty * e1x;
        const v = (dx * qx + dy * qy + dz * qz) * inv;
        if (v < -1e-6 || u + v > 1 + 1e-6) continue;
        const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
        if (t < 0 || t >= best) continue;
        best = t;
        bestTri = tri;
        bestU = u;
        bestV = v;
      }
      // Stop once the best hit lies before the exit of the current cell.
      const exit = Math.min(tMaxX, tMaxZ);
      if (bestTri >= 0 && best <= exit) break;
      if (exit > maxDist) break;
      if (tMaxX < tMaxZ) {
        ix += stepX;
        tMaxX += tDeltaX;
      } else {
        iz += stepZ;
        tMaxZ += tDeltaZ;
      }
      if (ix < 0 || iz < 0 || ix >= this.nx || iz >= this.nz) break;
    }
    if (bestTri < 0) return null;
    const o = bestTri * 9;
    const w = 1 - bestU - bestV;
    let nx = this.nrm[o]! * w + this.nrm[o + 3]! * bestU + this.nrm[o + 6]! * bestV;
    let ny = this.nrm[o + 1]! * w + this.nrm[o + 4]! * bestU + this.nrm[o + 7]! * bestV;
    let nz = this.nrm[o + 2]! * w + this.nrm[o + 5]! * bestU + this.nrm[o + 8]! * bestV;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    const f = bestTri * 3;
    return {
      t: best,
      point: [ox + dx * best, oy + dy * best, oz + dz * best],
      normal: [nx, ny, nz],
      face: [this.face[f]!, this.face[f + 1]!, this.face[f + 2]!],
      tri: bestTri,
      flags: this.flags[bestTri]!,
    };
  }

  /** Collects triangles penetrating a sphere, pushing out from their front side only. */
  public sphere(center: V3, radius: number, out: Contact[]): Contact[] {
    out.length = 0;
    this.query = (this.query + 1) >>> 0;
    if (this.query === 0) {
      this.stamp.fill(0);
      this.query = 1;
    }
    const [cx, cy, cz] = center;
    const ix0 = this.cellX(cx - radius);
    const ix1 = this.cellX(cx + radius);
    const iz0 = this.cellZ(cz - radius);
    const iz1 = this.cellZ(cz + radius);
    const closest: V3 = [0, 0, 0];
    for (let ix = ix0; ix <= ix1; ix += 1) {
      for (let iz = iz0; iz <= iz1; iz += 1) {
        const c = ix * this.nz + iz;
        for (let k = this.cellStart[c]!; k < this.cellStart[c + 1]!; k += 1) {
          const tri = this.cellItems[k]!;
          if (this.stamp[tri] === this.query) continue;
          this.stamp[tri] = this.query;
          const f = tri * 3;
          const fx = this.face[f]!;
          const fy = this.face[f + 1]!;
          const fz = this.face[f + 2]!;
          const o = tri * 9;
          const planeDist =
            (cx - this.pos[o]!) * fx + (cy - this.pos[o + 1]!) * fy + (cz - this.pos[o + 2]!) * fz;
          if (planeDist < -radius * 0.2 || planeDist > radius) continue;
          this.closestPoint(tri, cx, cy, cz, closest);
          const ddx = cx - closest[0];
          const ddy = cy - closest[1];
          const ddz = cz - closest[2];
          const dist = Math.hypot(ddx, ddy, ddz);
          if (dist >= radius) continue;
          let normal: V3;
          if (dist > 1e-5 && planeDist > 0) normal = [ddx / dist, ddy / dist, ddz / dist];
          else normal = [fx, fy, fz];
          const depth = planeDist > 0 ? radius - dist : radius - planeDist;
          out.push({ normal, face: [fx, fy, fz], depth, tri, flags: this.flags[tri]! });
        }
      }
    }
    return out;
  }

  private closestPoint(tri: number, px: number, py: number, pz: number, out: V3): void {
    const o = tri * 9;
    const ax = this.pos[o]!;
    const ay = this.pos[o + 1]!;
    const az = this.pos[o + 2]!;
    const bx = this.pos[o + 3]!;
    const by = this.pos[o + 4]!;
    const bz = this.pos[o + 5]!;
    const cx = this.pos[o + 6]!;
    const cy = this.pos[o + 7]!;
    const cz = this.pos[o + 8]!;
    const abx = bx - ax;
    const aby = by - ay;
    const abz = bz - az;
    const acx = cx - ax;
    const acy = cy - ay;
    const acz = cz - az;
    const apx = px - ax;
    const apy = py - ay;
    const apz = pz - az;
    const d1 = abx * apx + aby * apy + abz * apz;
    const d2 = acx * apx + acy * apy + acz * apz;
    if (d1 <= 0 && d2 <= 0) {
      out[0] = ax;
      out[1] = ay;
      out[2] = az;
      return;
    }
    const bpx = px - bx;
    const bpy = py - by;
    const bpz = pz - bz;
    const d3 = abx * bpx + aby * bpy + abz * bpz;
    const d4 = acx * bpx + acy * bpy + acz * bpz;
    if (d3 >= 0 && d4 <= d3) {
      out[0] = bx;
      out[1] = by;
      out[2] = bz;
      return;
    }
    const vc = d1 * d4 - d3 * d2;
    if (vc <= 0 && d1 >= 0 && d3 <= 0) {
      const v = d1 / (d1 - d3);
      out[0] = ax + abx * v;
      out[1] = ay + aby * v;
      out[2] = az + abz * v;
      return;
    }
    const cpx = px - cx;
    const cpy = py - cy;
    const cpz = pz - cz;
    const d5 = abx * cpx + aby * cpy + abz * cpz;
    const d6 = acx * cpx + acy * cpy + acz * cpz;
    if (d6 >= 0 && d5 <= d6) {
      out[0] = cx;
      out[1] = cy;
      out[2] = cz;
      return;
    }
    const vb = d5 * d2 - d1 * d6;
    if (vb <= 0 && d2 >= 0 && d6 <= 0) {
      const w = d2 / (d2 - d6);
      out[0] = ax + acx * w;
      out[1] = ay + acy * w;
      out[2] = az + acz * w;
      return;
    }
    const va = d3 * d6 - d5 * d4;
    if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0) {
      const w = (d4 - d3) / (d4 - d3 + (d5 - d6));
      out[0] = bx + (cx - bx) * w;
      out[1] = by + (cy - by) * w;
      out[2] = bz + (cz - bz) * w;
      return;
    }
    const denom = 1 / (va + vb + vc);
    const v = vb * denom;
    const w = vc * denom;
    out[0] = ax + abx * v + acx * w;
    out[1] = ay + aby * v + acy * w;
    out[2] = az + abz * v + acz * w;
  }
}

export interface RailHit {
  rail: Rail;
  seg: number;
  /** Parameter along the segment, 0..1. */
  t: number;
  point: V3;
  dist: number;
}

/** Brute-force nearest grind segment lookup; the park has a few hundred segments. */
export class RailNet {
  private readonly segments: { rail: Rail; seg: number; a: V3; b: V3 }[] = [];

  public constructor(public readonly rails: Rail[]) {
    for (const rail of rails) {
      const count = rail.loop ? rail.points.length : rail.points.length - 1;
      for (let seg = 0; seg < count; seg += 1) {
        this.segments.push({
          rail,
          seg,
          a: rail.points[seg]!,
          b: rail.points[(seg + 1) % rail.points.length]!,
        });
      }
    }
  }

  public get segmentCount(): number {
    return this.segments.length;
  }

  public nearest(p: V3, radius: number, accept?: (hit: RailHit) => boolean): RailHit | null {
    let best: RailHit | null = null;
    for (const s of this.segments) {
      const [ax, ay, az] = s.a;
      const abx = s.b[0] - ax;
      const aby = s.b[1] - ay;
      const abz = s.b[2] - az;
      // Quick reject on the segment's bounding box.
      if (
        p[0] < Math.min(ax, s.b[0]) - radius ||
        p[0] > Math.max(ax, s.b[0]) + radius ||
        p[2] < Math.min(az, s.b[2]) - radius ||
        p[2] > Math.max(az, s.b[2]) + radius
      ) {
        continue;
      }
      const len2 = abx * abx + aby * aby + abz * abz;
      let t = len2 > 0 ? ((p[0] - ax) * abx + (p[1] - ay) * aby + (p[2] - az) * abz) / len2 : 0;
      t = Math.max(0, Math.min(1, t));
      const point: V3 = [ax + abx * t, ay + aby * t, az + abz * t];
      const dist = Math.hypot(p[0] - point[0], p[1] - point[1], p[2] - point[2]);
      if (dist > radius || (best && dist >= best.dist)) continue;
      const hit: RailHit = { rail: s.rail, seg: s.seg, t, point, dist };
      if (accept && !accept(hit)) continue;
      best = hit;
    }
    return best;
  }
}

export interface RailCursor {
  seg: number;
  t: number;
}

export function railSegmentCount(rail: Rail): number {
  return rail.loop ? rail.points.length : rail.points.length - 1;
}

export function railSegment(rail: Rail, seg: number): [V3, V3] {
  const a = rail.points[seg]!;
  const b = rail.points[(seg + 1) % rail.points.length]!;
  return [a, b];
}

export function railPoint(rail: Rail, cursor: RailCursor): V3 {
  const [a, b] = railSegment(rail, cursor.seg);
  return [
    a[0] + (b[0] - a[0]) * cursor.t,
    a[1] + (b[1] - a[1]) * cursor.t,
    a[2] + (b[2] - a[2]) * cursor.t,
  ];
}

export function railTangent(rail: Rail, seg: number): V3 {
  const [a, b] = railSegment(rail, seg);
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  const l = Math.hypot(dx, dy, dz) || 1;
  return [dx / l, dy / l, dz / l];
}

/**
 * Moves a cursor `distance` metres along a rail (negative moves backwards). Returns the
 * signed leftover distance when the rail ends (0 while still on the rail).
 */
export function advanceRail(rail: Rail, cursor: RailCursor, distance: number): number {
  const count = railSegmentCount(rail);
  let remaining = distance;
  for (let guard = 0; guard < 4 * count + 8; guard += 1) {
    const [a, b] = railSegment(rail, cursor.seg);
    const len = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]) || 1e-6;
    const target = cursor.t + remaining / len;
    if (target >= 0 && target <= 1) {
      cursor.t = target;
      return 0;
    }
    if (target > 1) {
      remaining -= (1 - cursor.t) * len;
      if (cursor.seg + 1 < count) {
        cursor.seg += 1;
        cursor.t = 0;
      } else if (rail.loop) {
        cursor.seg = 0;
        cursor.t = 0;
      } else {
        cursor.t = 1;
        return remaining;
      }
    } else {
      remaining += cursor.t * len;
      if (cursor.seg > 0) {
        cursor.seg -= 1;
        cursor.t = 1;
      } else if (rail.loop) {
        cursor.seg = count - 1;
        cursor.t = 1;
      } else {
        cursor.t = 0;
        return remaining;
      }
    }
  }
  return 0;
}
